import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import {
  Modal,
  Loading,
  Form,
  Button,
  TextArea,
  Label,
} from "@gpstrackit/gtc-react-controls";
import { Typography } from "@mui/material";
import { LogManagerConsole } from "../../../../core/logManager";
import { NotificationManager } from "Components/Notifications";

import { close, reset, deleteVideoRequest } from "./redux/slice";
import { useDeleteClipRequests } from "Modules/video/.globals/api/clipRequests";

const DeleteNoteTextArea = ({ onClearValidationError, onChange, ...rest }) => (
  <TextArea
    {...rest}
    onChange={(nextValue) => {
      onClearValidationError?.();
      onChange?.(nextValue);
    }}
  />
);

export const DeleteVideoModal = ({ onClose }) => {
  const formDelete = Form.useForm();
  const [noteFieldError, setNoteFieldError] = useState("");
  const { messages } = useIntl();
  const dispatch = useDispatch();

  const { loading: loadingRedux, open, type, items, result } = useSelector(
    (state) => state.videoDeleteRedux,
  );
  const { mutate: deleteClips, isPending: deletingClips } = useDeleteClipRequests();
  const loading = loadingRedux || deletingClips;
  LogManagerConsole("MEDIA", "DeleteVideoModal", loading, open, type, items);

  const handleClose = useCallback(
    (refresh) => {
      dispatch(close());
      formDelete.resetValues();
      setNoteFieldError("");

      onClose && onClose(refresh, type);
      dispatch(reset());
    },
    [dispatch, formDelete, onClose, type],
  );

  useEffect(() => {
    if (open) {
      setNoteFieldError("");
    }
  }, [open]);

  const getTitle = useCallback(() => {
    if (type === "notes") {
      return messages["videoDetail_deleteEventNotes"];
    } else if (type === "media") {
      return messages["videoDetail_deleteEvents"];
    }
    if (type === "") {
      return messages["videoDetail_deleteClips"];
    }
    return "";
  }, [type, messages]);

  useEffect(() => {
    if (result) {
      dispatch(reset({ result: null }));

      notifyDeleted(result?.action);

      handleClose(false);
    }
  }, [result]);

  function notifyDeleted(action) {
      if (action == "notes") {
        NotificationManager.success(
          messages["videoDetail_deletedNotes"],
          messages["success"],
          3000,
          null,
          null,
          "success",
          null,
        );
      } else {
        NotificationManager.success(
          messages["videoDetail_deleteSuccess"],
          action == "media"
            ? messages["videoDetail_deleteEvents"]
            : messages["videoDetail_deleteClips"],
          3000,
          null,
          null,
          "success",
          null,
        );
      }

  }

  return (
    <Modal
      title={getTitle()}
      closeButton
      open={open}
      position="right"
      size="small"
      handleClose={() => {
        handleClose();
      }}
      footer={
        <div style={{ alignContent: "start" }}>
          <Button
            isDisabled={loading}
            onClick={(e) => {
              handleClose();
            }}
            variant="secondary"
          >
            {messages["cancel"]}
          </Button>
          {"  "}
          <Button
            isDisabled={items?.length == 0 || loading}
            onClick={(e) => {
              formDelete.submit();
            }}
            variant="primary"
          >
            {messages["delete"]}
          </Button>
        </div>
      }
    >
      <div>
        {loading && (
          <div style={{ paddingLeft: 20 }}>
            <Loading full className={"loading-training-lms"} />
          </div>
        )}
        <div style={{ padding: 24 }}>
          <Typography variant="subtitle2" color="textSecondary">
            {messages["videoDetail_deleteMediaWarning"]}
          </Typography>

          {items?.length && (
            <ul>
              {items?.map((item, key) => {
                return <li key={key}>{item?.label}</li>;
              })}
            </ul>
          )}
          <Form
            ref={formDelete.ref}
            onValidSubmit={(values) => {
              const note = String(values?.note || "");
              const nonSpaceChars = note.replace(/\s/g, "").length;

              setNoteFieldError("");

              if (type === "media" && nonSpaceChars < 6) {
                setNoteFieldError(messages["videoDetail_notes_maxLength"]);
                return;
              }

              if (type === "clip") {
                deleteClips(
                  items.map((i) => i.id),
                  {
                    onSuccess: () => {
                      notifyDeleted("clip");
                      handleClose(false);
                    },
                    onError: () =>
                      NotificationManager.error(messages["errorMessage"], messages["error"], 3000, null, null, "warning"),
                  },
                );
                return;
              }

              dispatch(
                deleteVideoRequest({
                  ids: items.map((i) => i.id),
                  note: note.trimStart(),
                }),
              );
            }}
          >
            {type === "media" && (
              <>
                <Form.Item
                  name="note"
                  label={messages["videoDetail_notes"]}
                  rules={[
                    {
                      required: type === "media" ? true : false,
                      message: "required_field",
                    },
                  ]}
                >
                  <DeleteNoteTextArea
                    onClearValidationError={() => setNoteFieldError("")}
                    id="note"
                    placeholder={messages["videoDetail_notes"]}
                    minLength={6}
                    maxLength={300}
                  />
                </Form.Item>
                {noteFieldError ? (
                  <Typography style={{ marginTop: "-15px" }} variant="caption" component="p" color="error">
                    {noteFieldError}
                  </Typography>
                ) : (
                  <Label
                    size="sm"
                    style={{
                      color: "gray",
                      marginTop: "-15px",
                      position: "absolute",
                    }}
                  >
                    {messages["videoDetail_notes_maxLength"]}
                  </Label>
                )}
              </>
            )}
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteVideoModal;
